import React, { useEffect, useState, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  ShieldAlert, Plus, Mic, Square, Loader2, FileText, Upload, FileCheck2, Sparkles, RotateCcw,
} from "lucide-react";
import { toast } from "sonner";

const STATUS = {
  REGISTRADA: { cls: "bg-blue-100 text-blue-700", label: "Registrada" },
  AGUARDANDO_ASSINATURA: { cls: "bg-amber-100 text-amber-700", label: "Aguardando assinatura" },
  DOCUMENTO_ASSINADO: { cls: "bg-green-100 text-green-700", label: "Documento assinado" },
  CANCELADA: { cls: "bg-slate-100 text-slate-500", label: "Cancelada" },
};

export default function Disciplinar() {
  const { scopeParams, selectedSchoolId } = useAuth();
  const [occ, setOcc] = useState(null);
  const [students, setStudents] = useState([]);
  const [manualOpen, setManualOpen] = useState(false);
  const [voiceOpen, setVoiceOpen] = useState(false);

  const fetchOcc = () => api.get("/occurrences", { params: scopeParams }).then((r) => setOcc(r.data));
  useEffect(() => {
    fetchOcc();
    api.get("/students", { params: scopeParams }).then((r) => setStudents(r.data));
    // eslint-disable-next-line
  }, [selectedSchoolId]);

  const downloadDoc = async (docId) => {
    try {
      const { data } = await api.get(`/disciplinar/files/${docId}`, { responseType: "blob" });
      window.open(URL.createObjectURL(data), "_blank");
    } catch (e) { toast.error("Não foi possível abrir o documento"); }
  };

  const genPdf = async (id) => {
    try {
      const { data } = await api.post(`/occurrences/${id}/generate-pdf`);
      toast.success("PDF provisório gerado");
      fetchOcc();
      downloadDoc(data.document_id);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const [uploadFor, setUploadFor] = useState(null);
  const fileRef = useRef();
  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !uploadFor) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/occurrences/${uploadFor}/signed-document`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Documento assinado anexado");
      fetchOcc();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { e.target.value = ""; setUploadFor(null); }
  };

  if (occ === null) return <div className="grid h-64 place-items-center"><Loader2 className="h-7 w-7 animate-spin text-blue-700" /></div>;

  return (
    <div>
      <input type="file" ref={fileRef} accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={onUpload} data-testid="signed-upload-input" />
      <PageHeader title="Acompanhamento Disciplinar" subtitle="Registro de ocorrências — manual ou por voz com IA">
        <Button variant="outline" onClick={() => setVoiceOpen(true)} data-testid="voice-occurrence-button"><Mic className="mr-1.5 h-4 w-4" /> Gravar por Voz</Button>
        <Button onClick={() => setManualOpen(true)} data-testid="manual-occurrence-button" className="bg-blue-700 hover:bg-blue-800"><Plus className="mr-1.5 h-4 w-4" /> Registrar Ocorrência</Button>
      </PageHeader>

      {occ.length === 0 ? <EmptyState icon={ShieldAlert} title="Nenhuma ocorrência registrada" description="Registre manualmente ou grave por voz." /> : (
        <Card className="overflow-x-auto border-slate-200" data-testid="occurrences-table">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Estudante</TableHead><TableHead>Data</TableHead><TableHead>Descrição</TableHead>
              <TableHead>Origem</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Ações</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {occ.map((o) => (
                <TableRow key={o.id} data-testid={`occurrence-row-${o.id}`}>
                  <TableCell className="font-medium text-slate-800">{o.student_name}<div className="text-xs text-slate-400">{o.registration}</div></TableCell>
                  <TableCell className="text-slate-500">{o.date_fact}{o.time_fact ? ` ${o.time_fact}` : ""}</TableCell>
                  <TableCell className="max-w-xs"><p className="line-clamp-2 text-sm text-slate-600">{o.description}</p></TableCell>
                  <TableCell>{o.origin === "VOZ" ? <Badge className="bg-violet-100 text-violet-700"><Mic className="mr-0.5 h-3 w-3" /> Voz+IA</Badge> : <Badge variant="outline">Manual</Badge>}</TableCell>
                  <TableCell><Badge className={STATUS[o.status]?.cls}>{STATUS[o.status]?.label}</Badge></TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button size="sm" variant="ghost" onClick={() => genPdf(o.id)} data-testid={`gen-pdf-${o.id}`} title="Gerar PDF para assinatura"><FileText className="h-4 w-4" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => { setUploadFor(o.id); setTimeout(() => fileRef.current?.click(), 0); }} data-testid={`upload-signed-${o.id}`} title="Anexar documento assinado"><Upload className="h-4 w-4" /></Button>
                      {(o.documents || []).map((d) => (
                        <Button key={d.id} size="sm" variant="ghost" onClick={() => downloadDoc(d.id)} title="Documento assinado"><FileCheck2 className="h-4 w-4 text-green-600" /></Button>
                      ))}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <ManualDialog open={manualOpen} onOpenChange={setManualOpen} students={students} onDone={fetchOcc} />
      <VoiceDialog open={voiceOpen} onOpenChange={setVoiceOpen} students={students} onDone={fetchOcc} />
    </div>
  );
}

function ManualDialog({ open, onOpenChange, students, onDone }) {
  const [form, setForm] = useState({ student_id: "", date_fact: "", time_fact: "", description: "" });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      await api.post("/occurrences", { ...form, origin: "MANUAL" });
      toast.success("Ocorrência registrada"); onOpenChange(false); setForm({ student_id: "", date_fact: "", time_fact: "", description: "" }); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>Registrar Ocorrência</DialogTitle></DialogHeader>
        <div className="grid gap-3">
          <div><Label>Estudante *</Label>
            <Select value={form.student_id} onValueChange={(v) => set("student_id", v)}>
              <SelectTrigger data-testid="manual-student-select"><SelectValue placeholder="Selecione" /></SelectTrigger>
              <SelectContent>{students.map((s) => <SelectItem key={s.id} value={s.id}>{s.full_name} — {s.class_name || "s/turma"}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Data do fato *</Label><Input type="date" data-testid="manual-date-input" value={form.date_fact} onChange={(e) => set("date_fact", e.target.value)} /></div>
            <div><Label>Horário</Label><Input type="time" value={form.time_fact} onChange={(e) => set("time_fact", e.target.value)} /></div>
          </div>
          <div><Label>Descrição *</Label><Textarea rows={5} data-testid="manual-description-input" value={form.description} onChange={(e) => set("description", e.target.value)} /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button><Button onClick={save} disabled={saving || !form.student_id || !form.date_fact || !form.description} data-testid="manual-save-button" className="bg-blue-700 hover:bg-blue-800">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Registrar"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function VoiceDialog({ open, onOpenChange, students, onDone }) {
  const [phase, setPhase] = useState("idle"); // idle | recording | processing | review
  const [transcript, setTranscript] = useState("");
  const [professional, setProfessional] = useState("");
  const [studentId, setStudentId] = useState("");
  const [dateFact, setDateFact] = useState("");
  const [busy, setBusy] = useState(false);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);

  const reset = () => { setPhase("idle"); setTranscript(""); setProfessional(""); setStudentId(""); setDateFact(""); chunksRef.current = []; };
  const close = () => { if (mediaRef.current && mediaRef.current.state !== "inactive") mediaRef.current.stop(); reset(); onOpenChange(false); };

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        await processAudio(new Blob(chunksRef.current, { type: "audio/webm" }));
      };
      mediaRef.current = mr;
      mr.start();
      setPhase("recording");
    } catch (e) { toast.error("Não foi possível acessar o microfone"); }
  };
  const stopRec = () => { if (mediaRef.current && mediaRef.current.state === "recording") mediaRef.current.stop(); setPhase("processing"); };

  const processAudio = async (blob) => {
    try {
      const fd = new FormData();
      fd.append("file", blob, "ocorrencia.webm");
      const { data: tr } = await api.post("/occurrences/voice/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setTranscript(tr.transcript);
      await organize(tr.transcript);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setPhase("idle"); }
  };

  const organize = async (text) => {
    setBusy(true);
    try {
      const { data } = await api.post("/occurrences/voice/organize", { transcript: text });
      setProfessional(data.professional_text || text);
      if (data.matched_student_id) setStudentId(data.matched_student_id);
      setPhase("review");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setPhase("review"); setProfessional(text); }
    finally { setBusy(false); }
  };

  const confirm = async () => {
    setBusy(true);
    try {
      await api.post("/occurrences", { student_id: studentId, date_fact: dateFact || new Date().toISOString().slice(0, 10), description: professional, origin: "VOZ", transcript });
      toast.success("Ocorrência registrada"); onDone(); close();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader><DialogTitle>Gravar Ocorrência por Voz</DialogTitle></DialogHeader>

        {phase === "idle" && (
          <div className="flex flex-col items-center py-8">
            <p className="mb-4 text-center text-sm text-slate-500">Fale naturalmente. Ex.: "Aluno João da Silva, turma 1001, ...". A IA irá transcrever e organizar o texto; nada é registrado sem sua confirmação.</p>
            <Button onClick={startRec} data-testid="voice-start-button" className="h-16 w-16 rounded-full bg-red-600 hover:bg-red-700"><Mic className="h-7 w-7" /></Button>
          </div>
        )}
        {phase === "recording" && (
          <div className="flex flex-col items-center py-8">
            <div className="mb-4 flex items-center gap-2 text-red-600"><span className="h-3 w-3 animate-pulse rounded-full bg-red-600" /> Gravando...</div>
            <Button onClick={stopRec} data-testid="voice-stop-button" className="h-16 w-16 rounded-full bg-slate-800 hover:bg-slate-900"><Square className="h-6 w-6" /></Button>
          </div>
        )}
        {phase === "processing" && (
          <div className="flex flex-col items-center py-10"><Loader2 className="h-8 w-8 animate-spin text-blue-700" /><p className="mt-3 text-sm text-slate-500">Transcrevendo e organizando com IA...</p></div>
        )}
        {phase === "review" && (
          <div className="space-y-4">
            <div>
              <Label className="text-xs text-slate-500">Transcrição original (bruta)</Label>
              <div className="mt-1 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600" data-testid="voice-transcript">{transcript || "—"}</div>
            </div>
            <div>
              <Label className="flex items-center gap-1 text-blue-700"><Sparkles className="h-4 w-4" /> Texto profissional sugerido (editável)</Label>
              <Textarea rows={5} data-testid="voice-professional-input" value={professional} onChange={(e) => setProfessional(e.target.value)} className="mt-1" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Estudante *</Label>
                <Select value={studentId} onValueChange={setStudentId}>
                  <SelectTrigger data-testid="voice-student-select"><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>{students.map((s) => <SelectItem key={s.id} value={s.id}>{s.full_name} — {s.class_name || "s/turma"}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Data do fato</Label><Input type="date" value={dateFact} onChange={(e) => setDateFact(e.target.value)} /></div>
            </div>
            <div className="rounded-lg bg-blue-50 p-2 text-xs text-blue-700">A IA atua apenas como transcritora/organizadora/revisora. Revise antes de confirmar.</div>
          </div>
        )}

        <DialogFooter className="flex-wrap gap-2">
          <Button variant="outline" onClick={close}>Cancelar</Button>
          {phase === "review" && <Button variant="outline" onClick={() => organize(transcript)} disabled={busy} data-testid="voice-reprocess-button"><RotateCcw className="mr-1.5 h-4 w-4" /> Reprocessar</Button>}
          {phase === "review" && <Button onClick={confirm} disabled={busy || !studentId || !professional} data-testid="voice-confirm-button" className="bg-blue-700 hover:bg-blue-800">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirmar e Registrar"}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
